import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db8dho3k13ra8mig8ptg";
const MAX_MESSAGE = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const payload = new FormData(form);

    // Honeypot check: bots that fill the hidden field are silently dropped.
    const honeypot = String(payload.get("contact_alt") || "").trim();
    if (honeypot) {
      form.reset();
      setMessage("");
      setStatus("success");
      return;
    }
    payload.delete("contact_alt");

    if (message.length > MAX_MESSAGE) {
      setStatus("error");
      setFormError(`反馈内容不能超过 ${MAX_MESSAGE} 字，请精简后再提交。`);
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (typeof value === "string") body.append(key, value);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        form.reset();
        setMessage("");
        setStatus("success");
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交未通过校验，请检查填写内容后重试。"
            : serverMsg || "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-600">
          <i className="ri-checkbox-circle-line" />
        </div>
        <h3 className="text-lg font-semibold text-foreground-950">感谢你的反馈</h3>
        <p className="mt-2 max-w-sm text-sm text-foreground-600">
          我们已收到你的留言，后续会认真阅读。如需继续反馈，可点击下方按钮再次填写。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          再次填写
        </button>
      </div>
    );
  }

  return (
    <form
      id="nm1-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-1.5 block text-xs font-medium text-foreground-700">
            称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的称呼"
            className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-1.5 block text-xs font-medium text-foreground-700">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复（如需要）"
            className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-type" className="mb-1.5 block text-xs font-medium text-foreground-700">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="feedback_type"
          defaultValue="内容纠错"
          className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
        >
          <option value="内容纠错">内容纠错</option>
          <option value="剧情建议">剧情建议</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="fb-message" className="block text-xs font-medium text-foreground-700">
            反馈内容
          </label>
          <span className={`text-xs ${message.length > MAX_MESSAGE ? "text-accent-600" : "text-foreground-400"}`}>
            {message.length} / {MAX_MESSAGE}
          </span>
        </div>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={MAX_MESSAGE}
          value={message}
          onChange={(event) => setMessage(event.target.value.slice(0, MAX_MESSAGE))}
          rows={5}
          placeholder="请描述你遇到的问题或想法（不超过 500 字）"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-300 placeholder:text-foreground-400"
        />
      </div>

      {/* anti-spam honeypot */}
      <input
        className="anti-bot-field"
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && formError ? (
        <p className="mt-4 rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-700">{formError}</p>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
        <p className="text-xs text-foreground-400">本表单仅用于收集反馈，不会用于其他用途。</p>
      </div>
    </form>
  );
}